/**
 * Picker-side encoding for `(kind, schema, name)` exclusion triples.
 *
 * P2 — the legacy `${kind} ${schema} ${name}` (space-joined) shape used in
 * the picker components collides on legal SQL identifiers that contain
 * spaces. For example, `(table, "a b", "c")` and `(table, "a", "b c")`
 * both encode to `"table a b c"`, so when the user ticks both rows in the
 * ExclusionPickerDialog one of them is silently dropped on submit.
 *
 * Wave-3 T14 fixed the same collision on the server side (databaseBlueprintIpc
 * set-exclusions / update-settings) by switching to a NUL-separated key.
 * This helper extends the same encoding to the UI surfaces so the
 * round-trip from picker → selection-set → submitted `excludedObjects`
 * never collapses distinct rows.
 *
 * NUL (`\x00`) is illegal inside SQL identifiers across MSSQL / PG / MySQL
 * / Oracle / SQLite, so it is a safe in-string delimiter; the resulting
 * key is collision-free for every legal identifier triple.
 */

import type { BlueprintExcludedObject } from '../types/databaseBlueprintTypes';

/**
 * Unambiguous key for picker set membership.
 */
export function exclusionRowKey(r: {
  kind: string;
  schema: string;
  name: string;
}): string {
  return `${r.kind}\x00${r.schema}\x00${r.name}`;
}

/**
 * Build the submitted `excludedObjects` list from a candidate row list +
 * the picker's ticked-key set.
 *
 * Preserves input order, deduplicates true duplicates (same kind +
 * schema + name appearing twice in the input), and never collapses
 * rows that the legacy space-joined encoding would have collided.
 */
export function buildSubmittedExclusions(
  rows: ReadonlyArray<BlueprintExcludedObject>,
  tickedKeys: ReadonlySet<string>,
): BlueprintExcludedObject[] {
  const seen = new Set<string>();
  const out: BlueprintExcludedObject[] = [];
  for (const r of rows) {
    const k = exclusionRowKey(r);
    if (!tickedKeys.has(k)) continue;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ kind: r.kind, schema: r.schema, name: r.name });
  }
  return out;
}
